import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import moment from 'moment';
import firebase from '../../../utilities/firebase';
import store from '../../../utilities/store';
import { StateManager } from '../../../utilities/stateManager';
import { useSelectMenuProps } from '../../../utilities/ModalStackContext';
import {
  CAR_SOURCES,
  CAR_SOURCE_ORIGINS,
  UNKNOWN_CAR_SOURCE_LABEL,
  buildCarSourceUpdate,
  carSourceLabel,
  carSourceRequired,
  countPriorCarStocks,
  isValidCarSource,
  suggestCarSource,
} from '../../../utilities/carSource';

const ORIGIN_TEXT = {
  [CAR_SOURCE_ORIGINS.LEAD_FIRST_TOUCH]: 'lead first touch',
  [CAR_SOURCE_ORIGINS.LEAD_SOURCE]: 'lead source',
  [CAR_SOURCE_ORIGINS.DEALER_CONSIGNOR]: 'dealer consignor',
  [CAR_SOURCE_ORIGINS.CUSTOMER_HISTORY]: 'earlier cars',
};

const loadSuggestion = async (consignor, stockNumber) => {
  const phone = consignor.phone_number || consignor.phone || '';
  const [leadLookup, priorCarCount] = await Promise.all([
    phone
      ? Promise.resolve(store.lookupLeadsByPhone(phone)).catch(() => ({ success: false }))
      : Promise.resolve({ success: false }),
    store.getCustomerCarStocks(consignor.id)
      .then(stocks => countPriorCarStocks(stocks, stockNumber))
      .catch(() => 0),
  ]);
  const leads = leadLookup && leadLookup.success && Array.isArray(leadLookup.leads) ? leadLookup.leads : [];
  return suggestCarSource({ consignor, leads, priorCarCount });
};

export default function CarSourceField({ car = {} }) {
  const selectMenuProps = useSelectMenuProps();
  const stockNumber = car.stock;
  const consignor = car.consignor && typeof car.consignor === 'object' ? car.consignor : {};
  const saved = isValidCarSource(car.car_source) ? car.car_source : '';
  const required = carSourceRequired(car);

  const [suggestion, setSuggestion] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    setSuggestion(null);
    if (!consignor.id) return undefined;
    loadSuggestion(consignor, stockNumber)
      .then(s => { if (!cancelled) setSuggestion(s); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consignor.id, stockNumber]);

  const save = (key) => {
    const update = buildCarSourceUpdate({
      key,
      suggestion,
      user: { id: StateManager.userID, name: StateManager.userName },
    });
    // Optimistic: show the choice now, write in the background.
    const previous = Object.keys(update).reduce((acc, k) => ({ ...acc, [k]: car[k] === undefined ? null : car[k] }), {});
    StateManager.updateCar(update);
    firebase.firestore().doc('cars/' + stockNumber).set(update, { merge: true }).catch(err => {
      StateManager.updateCar(previous);
      StateManager.setAlertAndOpen('Could not save the car source: ' + (err.message || err), 'error');
    });
  };

  // Unsaved suggestion is pre-selected but still needs a click to save.
  const pending = !saved && suggestion ? suggestion.key : '';
  const value = saved || pending;
  const showError = required && !saved;

  let helper;
  if (saved) {
    const who = car.car_source_set_by_name ? ` by ${car.car_source_set_by_name}` : '';
    const when = car.car_source_set_at ? ` on ${moment(car.car_source_set_at).format('MMM D, YYYY')}` : '';
    if (car.car_source_auto) {
      helper = `Auto-filled from ${ORIGIN_TEXT[car.car_source_origin] || 'suggestion'}${car.car_source_detail ? ` (${car.car_source_detail})` : ''} · confirmed${who}${when}`;
    } else if (car.car_source_overridden) {
      helper = `Set${who}${when} · overrode suggestion ${carSourceLabel(car.car_source_suggested)}`;
    } else {
      helper = `Set${who}${when}`;
    }
  } else if (pending) {
    helper = `Suggested from ${ORIGIN_TEXT[suggestion.origin] || 'records'}${suggestion.detail ? ` (${suggestion.detail})` : ''}. Confirm or pick another.`;
  } else {
    helper = required ? 'Required before the agreement can be sent' : UNKNOWN_CAR_SOURCE_LABEL;
  }

  return (
    <Box sx={{ px: 2, pb: 2 }} data-testid="car-source-field">
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <FormControl fullWidth size="small" required={required} error={showError}>
          <InputLabel id="car-source-label">How did they find us?</InputLabel>
          <Select
            labelId="car-source-label"
            value={value}
            label="How did they find us?"
            onChange={(e) => save(e.target.value)}
            MenuProps={selectMenuProps}
            displayEmpty={false}
            renderValue={(v) => (v ? carSourceLabel(v) : '')}
          >
            {CAR_SOURCES.map(s => (
              <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
            ))}
          </Select>
          <FormHelperText sx={!saved && !pending && !required ? { color: 'warning.dark' } : undefined}>
            {helper}
          </FormHelperText>
        </FormControl>
        {pending && (
          <Button size="small" variant="contained" sx={{ mt: 0.5, flexShrink: 0 }} onClick={() => save(pending)}>
            Confirm
          </Button>
        )}
      </Box>
    </Box>
  );
}
